'use client'

import { useEffect, useRef, useState } from 'react'
import { usePrerenderSafeSearchParams } from '@/hooks/use-prerender-safe-search-params'
import { FlaskConical } from 'lucide-react'
import { SoilTestForm, type SoilTestSubmissionResult } from '@/components/tools/soil-test-form'
import { SoilTestResults } from '@/components/tools/soil-test-results'
import { useAuth } from '@/contexts/AuthContext'
import { useNewsletterPrompt } from '@/hooks/useNewsletterPrompt'
import { NewsletterSignupModal } from '@/components/ui/newsletter-signup-modal'
import {
  HeadlineHero,
  LawnPrefillBanner,
  RelatedTools,
  ResultEmailCaptureCard,
  ToolPageShell,
  useResultHandoff,
} from '@/components/tools/shared'
import { useDefaultLawn } from '@/hooks/use-default-lawn'
import { useFromTool } from '@/hooks/use-from-tool'
import { parseGrassType, parseLawnSize, parseZipCode } from '@/hooks/useToolFromQuery'
import { analytics, stashEmailCaptureAttribution } from '@/lib/analytics'
import { buildToolUrl } from '@/lib/result-tool-urls'
import { guideSlugToGrassName } from '@/lib/grass-slug'
import { useCalculatorStarted } from '@/hooks/useCalculatorStarted'

export function SoilTestAnalyzerPageContent() {
  const searchParams = usePrerenderSafeSearchParams()
  useFromTool('soil-test-analyzer')
  const { user, subscription } = useAuth()
  const { lawns, selectedLawnId, setSelectedLawnId, prefill: lawnPrefill } = useDefaultLawn()
  const [result, setResult] = useState<SoilTestSubmissionResult | null>(null)
  const resultRef = useResultHandoff(result)
  const {
    trackUsage,
    showNewsletterModal,
    closeNewsletterModal,
    usageCount,
  } = useNewsletterPrompt()
  const trackedUsageRef = useRef(false)
  // calculator_started on first input — this tool gates calculator_used on an
  // explicit report submit, so it can't see users who start entering but never
  // submit. The page body is the input container.
  const startedRef = useCalculatorStarted('soil-test-analyzer')
  const urlZip = parseZipCode(searchParams.get('zip') ?? '')
  const urlGrass = parseGrassType(searchParams.get('grass') ?? '')
  const urlLawnSize = parseLawnSize(searchParams.get('size') ?? '')

  useEffect(() => {
    if (!result || trackedUsageRef.current) return
    trackedUsageRef.current = true
    trackUsage('soil-test-analyzer')
    analytics.calculatorUsed('soil-test-analyzer', {
      inputs: {
        lab: result.values.lab,
        ph: result.values.ph ?? undefined,
        phosphorus_ppm: result.values.phosphorusPpm ?? undefined,
        potassium_ppm: result.values.potassiumPpm ?? undefined,
        organic_matter_pct: result.values.organicMatterPct ?? undefined,
        cec: result.values.cec ?? undefined,
      },
      result: {
        parsing_confidence: result.values.parsingConfidence,
        degraded: result.interpretation.degraded,
      },
      source: 'form_submit',
    })
  }, [result, trackUsage])

  return (
    <ToolPageShell
      breadcrumbLabel="Soil Test Analyzer"
      rootRef={startedRef}
      after={
        <NewsletterSignupModal
          isOpen={showNewsletterModal}
          onClose={closeNewsletterModal}
          toolName="Soil Test Analyzer"
          usageCount={usageCount}
          grassType={result?.analysisContext?.grassType}
          zipCode={result?.analysisContext?.zipCode}
        />
      }
    >
      {/* The verdict in SoilTestResults carries the page H1 once results land,
          so this hero shows only before a report is analyzed. */}
      {!result && (
        <>
          <HeadlineHero
            eyebrow="Free tool, no signup"
            eyebrowIcon={<FlaskConical className="h-3.5 w-3.5" aria-hidden="true" />}
            title="Turn your soil test into an"
            accent="action plan"
            subtitle="Upload any US lawn soil test PDF. Get plain-English fixes with products and rates sized to your lawn."
          />
          <LawnPrefillBanner lawns={lawns} selectedLawnId={selectedLawnId} onSelect={setSelectedLawnId} />
          <SoilTestForm
            initialValues={{
              grassType: urlGrass ?? lawnPrefill.grassType,
              zipCode: urlZip ?? lawnPrefill.zipCode,
              lawnSize: urlLawnSize ?? lawnPrefill.lawnSizeSqft,
              lawnId: selectedLawnId ?? undefined,
            }}
            onSuccess={setResult}
          />
        </>
      )}

      {result && (
        <div ref={resultRef} className="mt-4 scroll-mt-20 space-y-6">
          <SoilTestResults
            values={result.values}
            interpretation={result.interpretation}
            analysisContext={result.analysisContext}
            soilTestId={result.soilTestId}
            claimToken={result.claimToken}
            isAuthenticated={!!user}
            isPremium={!!subscription?.isPremium}
            onReset={() => setResult(null)}
          />
          <ResultEmailCaptureCard
            source="tool_result_soil_test"
            toolDisplayName="soil test plan"
            subcopy="Reminders to recheck your numbers before each season."
            grassType={result.analysisContext.grassType}
            zipCode={result.analysisContext.zipCode}
            accountLead={{
              redirectTo: buildToolUrl("soil-test-analyzer", {
                grassType: result.analysisContext.grassType,
                zip: result.analysisContext.zipCode,
                lawnSize: result.analysisContext.lawnSize,
              }),
              headline: (() => {
                const grassName = guideSlugToGrassName(result.analysisContext.grassType)
                return grassName
                  ? `Save my soil test plan for ${grassName}`
                  : "Save my soil test plan"
              })(),
              subcopy: "Keep this soil test plan in a free account and track your lawn over time.",
              dividerLabel: "or get your 12-month calendar by email",
              onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_soil_test"),
            }}
            onCaptured={() => {
              closeNewsletterModal()
            }}
          />
        </div>
      )}

      <RelatedTools
        className="mt-10"
        currentToolId="soil-test-analyzer"
        prefillValues={{
          zip: result?.analysisContext.zipCode ?? lawnPrefill.zipCode,
          grassType: result?.analysisContext.grassType ?? lawnPrefill.grassType,
          lawnSize: result?.analysisContext.lawnSize ?? lawnPrefill.lawnSizeSqft,
        }}
      />
    </ToolPageShell>
  )
}

// (Education card content now renders server-side via ToolContentFooter; see
// data/tool-content/soil-test-analyzer.ts.)
